import { useState, useEffect } from 'react';

const IMMERSIVE_EVENT = 'tgo-immersive-change';
const ROUTE_CHANGE_EVENT = 'tgo-route-change';

let activeFullscreenGameCount = 0;

/**
 * Notify global listeners whenever React Router changes location.
 */
export const notifyRouteChanged = (): void => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(ROUTE_CHANGE_EVENT));
    }
};

/**
 * Toggle immersive game state for modal/fullscreen games embedded inside Lesson views
 * (e.g. MemoryGame, MemoryGame3D, QuizArchery, VoiceDefender, HeroVoice, BunnyHop, etc.).
 */
export const setImmersiveGameOverlay = (isActive: boolean): () => void => {
    if (isActive) {
        activeFullscreenGameCount += 1;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(IMMERSIVE_EVENT));
        }
        return () => {
            activeFullscreenGameCount = Math.max(0, activeFullscreenGameCount - 1);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event(IMMERSIVE_EVENT));
            }
        };
    }
    return () => {};
};

/**
 * Returns true if the given pathname/location corresponds to an immersive focus screen
 * where floating widgets (TiGO, Feedback Góp ý, Global Toolkit) should be hidden:
 * - Bảng Trắng PRO (/teacher/whiteboard)
 * - Màn hình chơi Games (/arcade/play/:slug, /student/arcade/play/:slug)
 */
export const isImmersiveFocusPath = (pathname?: string): boolean => {
    const raw = (
        pathname ??
        (typeof window !== 'undefined' ? window.location.pathname : '')
    ).toLowerCase();

    return (
        raw.includes('/whiteboard') ||
        raw.includes('/arcade/play')
    );
};

/**
 * React hook returning true whenever the user is on Whiteboard PRO, an Arcade Game Player route,
 * or an active fullscreen Lesson Game dialog.
 */
export const useIsImmersiveFocusMode = (routerPathname?: string): boolean => {
    const computeState = () =>
        activeFullscreenGameCount > 0 || isImmersiveFocusPath(routerPathname);

    const [isImmersive, setIsImmersive] = useState<boolean>(computeState);

    useEffect(() => {
        const sync = () => {
            setIsImmersive(activeFullscreenGameCount > 0 || isImmersiveFocusPath(routerPathname));
        };
        sync();

        window.addEventListener(IMMERSIVE_EVENT, sync);
        window.addEventListener(ROUTE_CHANGE_EVENT, sync);
        window.addEventListener('popstate', sync);
        window.addEventListener('hashchange', sync);

        return () => {
            window.removeEventListener(IMMERSIVE_EVENT, sync);
            window.removeEventListener(ROUTE_CHANGE_EVENT, sync);
            window.removeEventListener('popstate', sync);
            window.removeEventListener('hashchange', sync);
        };
    }, [routerPathname]);

    return isImmersive;
};
